import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, ArrowRight, CalendarClock, CheckCircle2, Loader2 } from "lucide-react";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { formatDateDisplay } from "@/lib/utils";
import {
  suggestEnrollmentWindow,
  mergeSelectedPlanIds,
} from "@/lib/quoting/enrollmentWindowDates";

type Draft = {
  planId: number;
  planName: string;
  carrier: string | null;
  coverageType: string | null;
  employerPays: number | null;
  employeePays: number | null;
  contributions: { employeePercent: number; dependentPercent: number } | null;
  alreadyLinked: boolean;
};

type DraftResponse = {
  drafts: Draft[];
  clientId: number | null;
  prospectId: number | null;
  needsClient: boolean;
  effectiveDate: string | null;
};

type Edit = { include: boolean; employeePercent: string; dependentPercent: string };

/** The window this client already has, if any. One row per client. */
type ExistingWindow = {
  startDate: string | null;
  endDate: string | null;
  selectedPlanIds: number[] | null;
} | null;

/** What was written, kept so the window screen can confirm it back. */
type Created = {
  clientId: number;
  planIds: number[];
  plans: {
    planId: number;
    planName: string;
    coverageType: string | null;
    split: string | null;
    /** The broker overrode what the client accepted — worth seeing at review. */
    changedFromAccepted: boolean;
  }[];
};

const money = (n: number | null) =>
  n == null ? "—" : `$${Math.round(n).toLocaleString()}`;

/** A typed percentage as a number, or null. Accepts a comma decimal separator. */
const percentValue = (raw: string): number | null => {
  const trimmed = raw.trim().replace(",", ".");
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

/**
 * The second screen: confirm what was written, then open enrollment.
 *
 * Creating the plans is only half the job — until a window exists, employees
 * see nothing. This is also the deliberate pause where the broker checks the
 * contributions look right before anyone enrols against them.
 */
function WindowStep({
  created,
  existingWindow,
  replacingDates,
  onReplaceDates,
  windowStart,
  windowEnd,
  onWindowStart,
  onWindowEnd,
  saving,
  onSave,
  onSkip,
}: {
  created: Created | null;
  existingWindow: ExistingWindow;
  replacingDates: boolean;
  onReplaceDates: () => void;
  windowStart: string;
  windowEnd: string;
  onWindowStart: (value: string) => void;
  onWindowEnd: (value: string) => void;
  saving: boolean;
  onSave: (keepExistingDates: boolean) => void;
  onSkip: () => void;
}) {
  const plans = created?.plans ?? [];
  // A window already saved for this client. The row is keyed on clientId alone,
  // so saving new dates replaces it for everyone already enrolling.
  const hasWindow = !!existingWindow?.startDate && !!existingWindow?.endDate;
  const showDateInputs = !hasWindow || replacingDates;
  const datesMissing = showDateInputs && (!windowStart || !windowEnd);
  const backwards = showDateInputs && !!windowStart && !!windowEnd && windowEnd < windowStart;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border divide-y">
        <p className="px-3 py-2 text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
          {plans.length} plan{plans.length === 1 ? "" : "s"} added
        </p>
        {plans.map((plan) => (
          <div key={plan.planId} className="flex items-center gap-3 px-3 py-2">
            <CoverageIcon planType={plan.coverageType} size="sm" />
            <p className="text-sm font-medium truncate flex-1 min-w-0">{plan.planName}</p>
            <p
              className={`text-[11px] shrink-0 tabular-nums flex items-center gap-1 ${
                plan.changedFromAccepted
                  ? "text-amber-700 dark:text-amber-500"
                  : "text-muted-foreground"
              }`}
              title={
                plan.changedFromAccepted
                  ? "You changed this from the split the client accepted"
                  : undefined
              }
            >
              {plan.changedFromAccepted && <AlertTriangle className="h-3 w-3 shrink-0" />}
              {plan.split ?? "No contribution set"}
            </p>
          </div>
        ))}
      </div>

      {hasWindow && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
          <p className="text-xs font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            This client is already enrolling
          </p>
          <p className="text-[11px] text-amber-800 dark:text-amber-300 mt-1">
            {formatDateDisplay(existingWindow!.startDate!)} to{" "}
            {formatDateDisplay(existingWindow!.endDate!)}, offering{" "}
            {existingWindow!.selectedPlanIds?.length ?? 0} plan
            {existingWindow!.selectedPlanIds?.length === 1 ? "" : "s"}. The plans above are not in
            it yet.
          </p>
          {!replacingDates && (
            <button
              type="button"
              onClick={onReplaceDates}
              className="text-[11px] underline text-amber-900 dark:text-amber-200 mt-2"
            >
              Change the dates instead
            </button>
          )}
        </div>
      )}

      {showDateInputs && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Enrollment opens</Label>
            <Input
              type="date"
              className="h-9 mt-1"
              value={windowStart}
              onChange={(e) => onWindowStart(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Deadline to choose</Label>
            <Input
              type="date"
              className="h-9 mt-1"
              value={windowEnd}
              onChange={(e) => onWindowEnd(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Must be before coverage starts
            </p>
          </div>
        </div>
      )}

      <DialogFooter className="sm:justify-between gap-3 border-t pt-4">
        <p className="text-xs text-muted-foreground">
          {backwards
            ? "The deadline can't be before enrollment opens."
            : datesMissing
              ? "Pick both dates."
              : "Employees can choose between these dates."}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onSkip} disabled={saving}>
            Not yet
          </Button>
          <Button
            disabled={saving || datesMissing || backwards}
            onClick={() => onSave(hasWindow && !replacingDates)}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CalendarClock className="h-4 w-4 mr-2" />
            )}
            {hasWindow && !replacingDates ? "Add to this window" : "Open enrollment"}
          </Button>
        </div>
      </DialogFooter>
    </div>
  );
}

/**
 * What an accepted quote will add to the client's benefits, shown before
 * anything is written. The plans already exist in the library, so this only
 * links them and records the contribution split the client agreed to.
 */
export function CreateClientPlansDialog({
  quoteId,
  open,
  onOpenChange,
}: {
  quoteId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { hasFeature } = useSubscriptionTier();

  // Two screens: pick the plans, then open the enrollment window. Creating the
  // plans alone does nothing for employees — they cannot choose until a window
  // exists, which the broker sets by hand so the pause doubles as a check on
  // contributions and setup.
  const [step, setStep] = useState<"plans" | "window">("plans");
  const [created, setCreated] = useState<Created | null>(null);

  // Editing dates when the client already has a window has to be asked for:
  // the row is keyed on clientId alone, so saving replaces it outright.
  const [replacingDates, setReplacingDates] = useState(false);
  const [windowStart, setWindowStart] = useState("");
  const [windowEnd, setWindowEnd] = useState("");

  const { data, isLoading } = useQuery<DraftResponse>({
    queryKey: ["/api/quotes", quoteId, "enrollment-draft"],
    queryFn: () =>
      apiRequest("GET", `/api/quotes/${quoteId}/enrollment-draft`).then((r) => r.json()),
    enabled: open && !!quoteId,
  });

  // Whether this client is already enrolling. Read from client_open_enrollment,
  // the source of truth — never from a flag on the quote, since wizardState is
  // replaced wholesale every time the wizard saves.
  const canOpenEnrollment = hasFeature("open_enrollment");
  const { data: existingWindow } = useQuery<ExistingWindow>({
    queryKey: ["/api/clients", data?.clientId, "open-enrollment"],
    queryFn: () =>
      apiRequest("GET", `/api/clients/${data?.clientId}/open-enrollment`).then((r) =>
        r.ok ? r.json() : null,
      ),
    enabled: open && !!data?.clientId && canOpenEnrollment,
  });

  // Closing with Escape, the X or the overlay goes straight to onOpenChange and
  // never reaches finish(), so without this a second visit would reopen on the
  // window step showing the last quote's plans.
  useEffect(() => {
    if (!open) return;
    setStep("plans");
    setCreated(null);
    setReplacingDates(false);
    setWindowStart("");
    setWindowEnd("");
  }, [open]);

  const [edits, setEdits] = useState<Record<number, Edit>>({});
  useEffect(() => {
    if (!data?.drafts) return;
    setEdits(
      Object.fromEntries(
        data.drafts.map((draft) => [
          draft.planId,
          {
            // A plan the client already has starts unticked: linking it twice
            // duplicates it and breaks their benefit config.
            include: !draft.alreadyLinked,
            employeePercent: draft.contributions ? String(draft.contributions.employeePercent) : "",
            dependentPercent: draft.contributions ? String(draft.contributions.dependentPercent) : "",
          },
        ]),
      ),
    );
  }, [data]);

  const included = (data?.drafts ?? []).filter((d) => edits[d.planId]?.include);

  const validPercent = (raw: string) => {
    if (raw.trim() === "") return true; // optional — broker can set them later
    const n = Number(raw.trim().replace(",", "."));
    return Number.isFinite(n) && n >= 0 && n <= 100;
  };
  const blocked =
    !included.length ||
    included.some((d) => {
      const edit = edits[d.planId];
      return !validPercent(edit.employeePercent) || !validPercent(edit.dependentPercent);
    });

  // Close and land on the client's benefits, with the new plans flagged so they
  // can be picked out of a long list.
  const finish = (clientId: number | null, planIds: number[]) => {
    onOpenChange(false);
    setStep("plans");
    setCreated(null);
    setReplacingDates(false);
    if (clientId) {
      navigate(`/clients/${clientId}?tab=benefits&newPlans=${planIds.join(",")}`);
    }
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = included.map((draft) => {
        const edit = edits[draft.planId];
        const employee = edit.employeePercent.trim().replace(",", ".");
        const dependent = edit.dependentPercent.trim().replace(",", ".");
        return {
          planId: draft.planId,
          // Both or neither: a contribution set for the employee tier but not
          // dependants would leave family enrolments with no employer share.
          ...(employee !== "" && dependent !== ""
            ? { employeePercent: Number(employee), dependentPercent: Number(dependent) }
            : {}),
        };
      });
      const res = await apiRequest("POST", `/api/quotes/${quoteId}/create-client-plans`, {
        plans: payload,
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error([body.message, body.detail].filter(Boolean).join(" — ") || "Failed");
      }
      return body;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", quoteId, "enrollment-draft"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", quoteId, "acceptance"] });

      const planIds: number[] = result.planIds ?? [];

      // The split is already in local state — the broker just confirmed it and
      // the write succeeded — so the confirmation needs no round-trip.
      setCreated({
        clientId: result.clientId,
        planIds,
        plans: included
          .filter((draft) => planIds.includes(draft.planId))
          .map((draft) => {
            const edit = edits[draft.planId];
            const employee = edit?.employeePercent.trim();
            const dependent = edit?.dependentPercent.trim();
            return {
              planId: draft.planId,
              planName: draft.planName,
              coverageType: draft.coverageType,
              split:
                employee && dependent
                  ? `Employee ${employee}% · Dependants ${dependent}%`
                  : null,
              changedFromAccepted:
                !!draft.contributions &&
                (percentValue(edit?.employeePercent ?? "") !==
                  draft.contributions.employeePercent ||
                  percentValue(edit?.dependentPercent ?? "") !==
                    draft.contributions.dependentPercent),
            };
          }),
      });

      // Without a window nobody can enrol, so that is the next step rather than
      // a line of advice in a toast. Agencies without the feature keep the old
      // behaviour, since the endpoint would 403 for them.
      if (result.clientId && canOpenEnrollment) {
        const suggested = suggestEnrollmentWindow(data?.effectiveDate);
        setWindowStart(suggested?.startDate ?? "");
        setWindowEnd(suggested?.endDate ?? "");
        setStep("window");
        return;
      }

      toast({
        title: "Plans added",
        description: `${planIds.length} plan${
          planIds.length === 1 ? "" : "s"
        } now offered to this client. Open an enrollment window when you're ready for employees to enrol.`,
      });
      finish(result.clientId, planIds);
    },
    onError: (error: Error) =>
      toast({ title: "Couldn't create the plans", description: error.message, variant: "destructive" }),
  });

  // Opening the window. Two shapes: keep the dates already saved and just add
  // the new plans to them, or write the dates from the inputs.
  const windowMutation = useMutation({
    mutationFn: async ({ keepExistingDates }: { keepExistingDates: boolean }) => {
      if (!created) throw new Error("Nothing was created");
      const startDate = keepExistingDates ? existingWindow?.startDate : windowStart;
      const endDate = keepExistingDates ? existingWindow?.endDate : windowEnd;
      if (!startDate || !endDate) throw new Error("Pick both dates");

      const res = await apiRequest(
        "PUT",
        `/api/clients/${created.clientId}/open-enrollment`,
        {
          startDate,
          endDate,
          // Never send the new ids alone: the window holds one flat list, so
          // that would drop whatever the client was already enrolling in.
          selectedPlanIds: mergeSelectedPlanIds(existingWindow?.selectedPlanIds, created.planIds),
        },
      );
      // apiRequest resolves on a 4xx, so the server's reason has to be read out
      // by hand — it names any plan whose coverage starts before the deadline.
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Couldn't open enrollment");
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", created?.clientId, "open-enrollment"],
      });
      toast({
        title: "Enrollment open",
        description: "Employees can now choose from these plans.",
      });
      finish(created?.clientId ?? null, created?.planIds ?? []);
    },
    onError: (error: Error) =>
      toast({
        title: "Couldn't open enrollment",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === "window" ? "Open enrollment for these plans" : "Add these plans to the client"}
          </DialogTitle>
          <DialogDescription>
            {step === "window"
              ? "The plans are saved. Employees still can't choose them until an enrollment window is open — check the split below looks right, then set the dates."
              : "The plans the client accepted, with the contribution split they chose. Nothing is created until you confirm."}
          </DialogDescription>
        </DialogHeader>

        {step === "window" ? (
          <WindowStep
            created={created}
            existingWindow={existingWindow ?? null}
            replacingDates={replacingDates}
            onReplaceDates={() => {
              // Editing an existing window starts from its own dates, not the
              // suggestion — the broker is adjusting what is there, and the
              // client may already be enrolling against it.
              if (existingWindow?.startDate) setWindowStart(existingWindow.startDate);
              if (existingWindow?.endDate) setWindowEnd(existingWindow.endDate);
              setReplacingDates(true);
            }}
            windowStart={windowStart}
            windowEnd={windowEnd}
            onWindowStart={setWindowStart}
            onWindowEnd={setWindowEnd}
            saving={windowMutation.isPending}
            onSave={(keepExistingDates) => windowMutation.mutate({ keepExistingDates })}
            onSkip={() => finish(created?.clientId ?? null, created?.planIds ?? [])}
          />
        ) : isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the acceptance…
          </div>
        ) : data?.needsClient ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            This quote belongs to a prospect. Convert them to a client first — plans are offered to
            clients, so there is nothing to attach these to yet.
          </div>
        ) : !data?.drafts.length ? (
          <p className="py-8 text-sm text-muted-foreground">
            No plans were recorded with this acceptance, so there's nothing to add.
          </p>
        ) : (
          <div className="space-y-2">
            {data.drafts.map((draft) => {
              const edit = edits[draft.planId];
              if (!edit) return null;
              // Compare by value, not text: 80, 80.0 and "80," are the same
              // split and should not be reported as a change from what the
              // client accepted.
              const contributionsChanged =
                !!draft.contributions &&
                (percentValue(edit.employeePercent) !== draft.contributions.employeePercent ||
                  percentValue(edit.dependentPercent) !== draft.contributions.dependentPercent);
              const update = (patch: Partial<Edit>) =>
                setEdits((prev) => ({ ...prev, [draft.planId]: { ...prev[draft.planId], ...patch } }));
              return (
                <div key={draft.planId} className="rounded-lg border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => update({ include: !edit.include })}
                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-muted/40 transition-colors ${
                      edit.include ? "" : "opacity-60"
                    }`}
                  >
                    <Checkbox checked={edit.include} className="pointer-events-none" />
                    <CoverageIcon planType={draft.coverageType} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{draft.planName}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{draft.carrier}</p>
                    </div>
                    {draft.alreadyLinked && (
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        Already offered
                      </Badge>
                    )}
                  </button>

                  {edit.include && (
                    <div className="border-t p-3 space-y-3">
                      {/* What the client saw when they accepted, so a wrong
                          contribution is obvious against the agreed totals. */}
                      <div className="rounded-md bg-muted/50 p-2.5 text-[11px] space-y-1">
                        <p className="text-muted-foreground">They accepted, per year</p>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Employer</span>
                          <span className="font-medium tabular-nums">{money(draft.employerPays)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Employee</span>
                          <span className="font-medium tabular-nums">{money(draft.employeePays)}</span>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs flex items-center justify-between gap-2">
                          <span>Employer contribution</span>
                          {/* The fields prefill from the client's own acceptance,
                              so say so — a bare editable number gives no clue
                              where it came from, and overwriting it would
                              otherwise lose what they agreed with no way back. */}
                          {draft.contributions && !contributionsChanged && (
                            <span className="text-[10px] font-normal text-muted-foreground flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3 text-primary" />
                              As the client chose
                            </span>
                          )}
                        </Label>
                        <div className="grid grid-cols-2 gap-3 mt-1">
                          <div>
                            <div className="relative">
                              <Input
                                className="h-9 pr-7 tabular-nums"
                                inputMode="decimal"
                                placeholder="Not set"
                                value={edit.employeePercent}
                                onChange={(e) => update({ employeePercent: e.target.value })}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                %
                              </span>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">Employee tier</p>
                          </div>
                          <div>
                            <div className="relative">
                              <Input
                                className="h-9 pr-7 tabular-nums"
                                inputMode="decimal"
                                placeholder="Not set"
                                value={edit.dependentPercent}
                                onChange={(e) => update({ dependentPercent: e.target.value })}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                %
                              </span>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">Dependants</p>
                          </div>
                        </div>
                        {draft.contributions && contributionsChanged && (
                          <div className="mt-1.5 flex items-start justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 dark:border-amber-800 dark:bg-amber-950/40">
                            <p className="text-[11px] text-amber-900 dark:text-amber-200 flex items-start gap-1">
                              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                              <span>
                                Different from what the client accepted —{" "}
                                <span className="tabular-nums font-medium">
                                  {draft.contributions.employeePercent}%
                                </span>{" "}
                                employee,{" "}
                                <span className="tabular-nums font-medium">
                                  {draft.contributions.dependentPercent}%
                                </span>{" "}
                                dependants.
                              </span>
                            </p>
                            <button
                              type="button"
                              className="text-[11px] underline shrink-0 text-amber-900 dark:text-amber-200"
                              onClick={() =>
                                update({
                                  employeePercent: String(draft.contributions!.employeePercent),
                                  dependentPercent: String(draft.contributions!.dependentPercent),
                                })
                              }
                            >
                              Restore
                            </button>
                          </div>
                        )}
                        {!draft.contributions && (
                          <p className="text-[11px] text-amber-700 dark:text-amber-500 mt-1.5 flex items-start gap-1">
                            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                            This quote was accepted before we recorded the split, so there's nothing
                            to pre-fill. Leave blank to set contributions later.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {step === "plans" && (
          <DialogFooter className="sm:justify-between gap-3 border-t pt-4">
            <p className="text-xs text-muted-foreground">
              {included.length === 0
                ? "Tick at least one plan."
                : blocked
                  ? "Contributions must be between 0 and 100."
                  : `${included.length} plan${included.length === 1 ? "" : "s"} will be offered to this client.`}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                disabled={blocked || createMutation.isPending || data?.needsClient}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                Add {included.length} plan{included.length === 1 ? "" : "s"}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
